import { conversion, identifyLead } from "../../lib/track";

import { Flag } from "../Flag";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, ArrowUpRight, Mail, Check, Globe, CalendarDays } from "lucide-react";
import ShareButton from "./Share";
import { useAssistant, Rich, Citations, DataViz } from "../../lib/assistant";
import { toast } from "sonner";
import { CANNED_ANSWERS, B2B_ANSWERS, HERO_HEADLINES, HERO_NEWS, DESTINATIONS } from "../../mock";
import { useCopy } from "../../lib/copy";
import { useIdle, sharedJson } from "../../lib/defer";
import { btnPrimary, btnGhost, BrandText } from "../Shared";
import { useSiteSearch, SearchDropdown, useSearchKeys } from "../SiteSearch";


function useTypewriter(groups, enabled, fromEmpty = false) {
  const [state, setState] = useState({ g: 0, q: 0, text: fromEmpty ? "" : groups[0].questions[0].text });
  useEffect(() => {
    setState({ g: 0, q: 0, text: fromEmpty && enabled ? "" : groups[0].questions[0].text });
    if (!enabled) return;
    let g = 0, q = 0, ci = fromEmpty ? 0 : groups[0].questions[0].text.length, dir = fromEmpty ? 1 : -1, t;
    const tick = () => {
      const full = groups[g].questions[q].text;
      if (dir === -1) {
        ci -= 1; setState({ g, q, text: full.slice(0, ci) });
        if (ci <= 0) {
          dir = 1; q += 1;
          if (q >= groups[g].questions.length) { q = 0; g = (g + 1) % groups.length; }
          setState({ g, q, text: "" }); t = setTimeout(tick, 700); return;
        }
        t = setTimeout(tick, 26);
      } else {
        ci += 1; setState({ g, q, text: full.slice(0, ci) });
        if (ci >= full.length) { dir = -1; t = setTimeout(tick, 4200); return; }
        t = setTimeout(tick, 55 + Math.random() * 40);
      }
    };
    t = setTimeout(tick, fromEmpty ? 400 : 4200);
    return () => clearTimeout(t);
  }, [groups, enabled, fromEmpty]);
  return state;
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const API = process.env.REACT_APP_BACKEND_URL;
export const TAG_FLAG = { UK: "🇬🇧", Canada: "🇨🇦", US: "🇺🇸", Australia: "🇦🇺", Schengen: "🇪🇺", UAE: "🇦🇪", Singapore: "🇸🇬", Japan: "🇯🇵", Germany: "🇩🇪", China: "🇨🇳", Agencies: "🌐" };

function timeAgo(iso) {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7); if (d < 60) return `${w}w ago`;
  return `${Math.round(d / 30)}mo ago`;
}

export function useNews(biz) {
  const [items, setItems] = useState(null);
  const idle = useIdle();
  useEffect(() => {
    if (!idle) return;
    let alive = true;
    const load = () => sharedJson(`${API}/api/news?audience=${biz ? "business" : "applicant"}&limit=15`)
      .then((d) => { if (alive && d.items?.length) setItems(d.items.map((n) => ({ ...n, when: timeAgo(n.published), live: true }))); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [biz, idle]);
  return items || HERO_NEWS.filter((n) => n.audience === "both" || n.audience === (biz ? "b2b" : "b2c"));
}

export function NewsStrip({ items, compact = false }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="hero-news">
      <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-foreground ${compact ? "h-8 bg-gray6" : "bg-background py-1.5 shadow-card"}`} data-testid="hero-news-label">
        <span className="h-1.5 w-1.5 rounded-full bg-danger animate-pulse" aria-hidden="true" />Latest
      </span>
      <div className="news-mask min-w-0 flex-1 overflow-hidden">
        <div className="news-track flex w-max">
          {[...items, ...items].map((n, i) => (
            <div key={`${n.title}-${i}`} role="link" tabIndex={i >= items.length ? -1 : 0} aria-hidden={i >= items.length} data-testid={i < items.length ? `hero-news-${i}` : undefined}
              onClick={() => navigate(n.to)} onKeyDown={(e) => e.key === "Enter" && navigate(n.to)}
              className={`mr-2 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-separator bg-background pl-3 pr-1 text-foreground transition-[background-color,box-shadow] hover:bg-background hover:shadow-pop ${compact ? "h-8 text-sm" : "py-1 text-sm shadow-card"}`}>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-tint-strong">{TAG_FLAG[n.tag] ? <Flag flag={TAG_FLAG[n.tag]} size={14} /> : <Globe className="h-3.5 w-3.5" aria-hidden="true" />}{n.tag}</span>
              <span>{n.title}</span>
              <span className="text-xs text-label2">{n.when}</span>
              {n.url ? (
                <a href={n.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={`Read on ${n.source}`} data-testid={i < items.length ? `hero-news-source-${i}` : undefined}
                  className="inline-flex items-center gap-1 rounded-full bg-tint/10 px-2.5 py-1 text-xs font-semibold text-tint-strong transition-colors hover:bg-tint hover:text-white">
                  {n.source} <ArrowUpRight className="h-3 w-3" />
                </a>
              ) : (
                <span className="rounded-full bg-gray6 px-2.5 py-1 text-xs font-medium text-label2">{n.source}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Newsletter({ biz }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email address"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, audience: biz ? "business" : "applicant", source: "hero" }) });
      if (!r.ok) throw new Error();
      setDone(true); identifyLead({ email, source: "newsletter:hero" }); conversion("briefing_subscribe", { source: "hero", audience: biz ? "business" : "applicant" }); toast.success("You're on the list. First briefing lands this week.");
    } catch { toast.error("Could not subscribe right now. Try again in a moment."); }
    finally { setBusy(false); }
  };
  if (done) return (
    <p className="flex items-center justify-center gap-2 text-sm text-label2" data-testid="newsletter-done"><Check className="h-4 w-4 text-success" /> Subscribed. Rule changes for {biz ? "agencies and sponsors" : "your passport"} arrive every Tuesday.</p>
  );
  return (
    <form onSubmit={submit} className="flex w-full justify-center sm:w-auto" data-testid="newsletter-form">
      <label className="flex h-11 w-full max-w-md items-center gap-2 rounded-full border border-separator bg-background pl-4 pr-1.5 text-sm transition-[border-color,box-shadow] focus-within:border-tint focus-within:shadow-[0_0_0_4px_rgba(0,136,255,0.12)]">
        <Mail className="h-4 w-4 shrink-0 text-label2" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={biz ? "Your work email" : "Your email"} title={biz ? "One email a week for agencies and sponsors" : "One email a week, unsubscribe any time"} aria-label="Email address" data-testid="newsletter-email"
          className="h-full min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-label3" />
        <button type="submit" disabled={busy} data-testid="newsletter-submit"
          className="h-8 shrink-0 rounded-full bg-primary px-4 text-xs font-semibold text-white transition-[background-color,transform] hover:bg-tint-strong active:scale-95 disabled:opacity-60">
          {busy ? "Adding…" : "Subscribe"}
        </button>
      </label>
    </form>
  );
}

export function answerFor(query, biz = false) {
  const q = query.toLowerCase();
  let best = null, bestScore = 0;
  for (const c of (biz ? B2B_ANSWERS : CANNED_ANSWERS)) {
    const score = c.match.filter((m) => q.includes(m)).length;
    if (score > bestScore) { bestScore = score; best = c; }
  }
  return best || (biz ? {
    source: "Vistolane for business",
    answer: "That depends on your visa routes and team size. The workspace covers intake, document chasing, deadline templates, client portals, invoicing and referrals for travel agencies, study consultants, immigration practices and sponsoring employers. Book a 30-minute demo and we set it up on your own cases, or compare workspace plans.",
  } : {
    source: "Vistolane requirements library",
    answer: "We track this in the requirements library for all 8 destinations, with documents, fees and timelines against each authority's published rules. Run the free eligibility check and every route for your passport is scored, or open the destination page for the full checklist.",
  });
}

const staticHeadline = (persona) => persona === "business"
  ? ["Visa management software and support services for agencies, consultants and employers.", "Tourist and student visas processed by Vistolane. Every other visa type carried with employer, agency, applicant and our team on one application track in VIMS."]
  : [`Online visa application assistance for ${DESTINATIONS.length} countries: requirements, fees and processing times.`, "The search engine for immigration: tell us where you want to go and we show the exact visa for your passport, the documents, the fee and the filing date we guarantee."];

// Layout B: fixed two-line block, verb pill above, crossfading copy (no typewriter).
function StableHeadline({ group, prompt }) {
  return (
    <div className="mx-auto max-w-3xl text-center" data-testid="hero-headline">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span key={group.verb} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}
          className="inline-flex h-7 items-center rounded-full bg-tint/10 px-3 text-xs font-semibold text-tint-strong" data-testid="hero-verb">{group.verb}</motion.span>
      </AnimatePresence>
      <div className="mt-3 grid min-h-[2.3em] place-items-center font-display text-4xl sm:text-5xl leading-[1.1] text-foreground">
        <AnimatePresence mode="wait" initial={false}>
          <motion.h1 key={prompt} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="col-start-1 row-start-1 [text-wrap:balance]">{prompt}</motion.h1>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Hero({ persona = "applicant", subtitle, ctas, news = true, below = null, layout = "classic", aside = null }) {
  const editorial = layout === "editorial";
  const stable = layout === "stable";
  const search = layout === "search";
  const heroKey = `vl-hero-${persona}`;
  const [query, setQuery] = useState(() => { try { return JSON.parse(sessionStorage.getItem(heroKey))?.q || ""; } catch { return ""; } });
  const [result, setResult] = useState(() => { try { const r = JSON.parse(sessionStorage.getItem(heroKey)); return r && !r.streaming ? r : null; } catch { return null; } });
  const [tagline, setTagline] = useState(true);
  useEffect(() => { if (!search) return; const t = setTimeout(() => setTagline(false), 9000); return () => clearTimeout(t); }, [search]);
  const showTagline = search && (tagline || !!result);
  useEffect(() => { if (result && !result.streaming) sessionStorage.setItem(heroKey, JSON.stringify(result)); else if (!result) sessionStorage.removeItem(heroKey); }, [result, heroKey]);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const biz = persona === "business";
  const lab = useCopy(biz ? "biz_hero" : "app_hero", { title: staticHeadline(persona)[0], desc: staticHeadline(persona)[1] });
  const sh = [lab.title, lab.desc];
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const groups = HERO_HEADLINES[persona];
  const tw = useTypewriter(groups, !reduced && !result && !(search && tagline), search);
  const group = groups[tw.g];
  const item = group.questions[tw.q];
  const prompt = cap(item.text);
  const isAsk = group.verb === "Ask";
  const newsItems = useNews(biz);
  const { send, busy } = useAssistant(biz ? "business" : "applicant");

  const firstRun = useRef(true);
  useEffect(() => { if (firstRun.current) { firstRun.current = false; return; } setResult(null); setQuery(""); }, [persona]);

  const ask = async (text) => {
    const q = (text ?? query).trim();
    if (!q || busy) return;
    setQuery(q);
    setResult({ q, answer: "", streaming: true, source: "" });
    const res = await send(q, (d) => setResult((r) => (r ? { ...r, answer: r.answer + d } : r)));
    if (res) setResult({ q, answer: res.text, scope: res.scope, source: res.citations?.[0]?.label || "Vistolane library", citations: res.citations, followups: res.followups, cta: res.cta, chart: res.chart, stats: res.stats, live: res.live });
  };
  // Typing shows matching guides, destinations and site pages; Enter on a short term opens the top match, a full question goes to the assistant.
  const [ddOpen, setDdOpen] = useState(false);
  const srch = useSiteSearch(query, { audience: biz ? "business" : "applicant", enabled: !result });
  const pickResult = (r) => { setDdOpen(false); navigate(r.to); };
  const askFromSearch = (t) => { setDdOpen(false); ask(t); };
  const words = query.trim().split(/\s+/).filter(Boolean).length;
  const keys = useSearchKeys({ items: srch.items, onPick: pickResult, onAsk: askFromSearch, open: ddOpen && !result, setOpen: setDdOpen, preferAsk: words > 4 || /\?$/.test(query.trim()) });
  const showDd = ddOpen && !result && query.trim().length >= 2;
  const go = () => {
    const q = query.trim();
    if (isAsk) { ask(q || prompt); return; }
    const to = item.to || group.to;
    navigate(q ? `${to}${to.includes("?") ? "&" : "?"}q=${encodeURIComponent(q)}` : to);
  };
  const reset = () => { setResult(null); setQuery(""); inputRef.current?.focus(); };

  return (
    <section ref={lab.ref} data-copy={lab.variant} onClickCapture={(e) => { if (e.target.closest(biz ? "a[href^='/contact']" : "a[href^='/eligibility'], a[href^='/destinations'], a[href^='/register'], button[type='submit']")) lab.click(); }} className={`hero-glow ${search && result ? "flex min-h-[calc(100vh-4rem)] flex-col justify-start" : ""}`} data-testid="hero" data-layout={layout} data-answering={!!result || undefined}>
      <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${search ? "pt-8 pb-6 sm:pt-12 sm:pb-8" : "pt-10 pb-10 sm:pt-14 sm:pb-14"} ${editorial ? "lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-start lg:gap-12" : ""}`}>
        <div className={editorial ? "min-w-0" : "contents"}>
        <AnimatePresence mode="wait">
          <motion.div key={persona} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} className={editorial ? "max-w-2xl text-left" : search ? "mx-auto w-full max-w-3xl text-center" : "mx-auto max-w-4xl text-center"}>
            {editorial ? (
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground [text-wrap:balance]" data-testid="hero-headline">{sh[0]}</h1>
            ) : search ? (
              <h1 className={`mx-auto font-display leading-[1.12] text-foreground [text-wrap:balance] ${result ? "text-3xl sm:text-4xl" : "grid min-h-[3.4em] place-items-center text-3xl sm:min-h-[2.3em] sm:text-4xl lg:text-[2.75rem]"}`} data-testid="hero-headline" aria-live="polite">
                <span className="col-start-1 row-start-1 w-full min-w-0 break-words [text-wrap:balance]">
                  {showTagline ? sh[0] : (<>
                    <span className="text-tint" data-testid="hero-verb">{group.verb}:</span>{" "}{tw.text}
                    <span className="ml-1 inline-block w-[3px] translate-y-1 animate-pulse bg-tint" style={{ height: "0.85em" }} aria-hidden="true" />
                  </>)}
                </span>
              </h1>
            ) : stable ? <StableHeadline group={group} prompt={prompt} /> : (
            <h1 className="grid w-full grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 sm:gap-x-4 text-left font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] sm:leading-[1.05] text-foreground min-h-[3.4em] sm:min-h-[3.15em]" data-testid="hero-headline" aria-live="polite">
              <span className="grid text-left sm:text-right text-tint" data-testid="hero-verb-col">
                {groups.map((g) => (
                  <span key={g.verb} className={`col-start-1 row-start-1 ${g.verb === group.verb ? "" : "invisible"}`} data-testid={g.verb === group.verb ? "hero-verb" : undefined} aria-hidden={g.verb !== group.verb}>{g.verb}:</span>
                ))}
              </span>
              <span className="min-w-0">{tw.text}<span className="ml-1 inline-block w-[3px] translate-y-1 animate-pulse bg-tint" style={{ height: "0.85em" }} aria-hidden="true" /></span>
            </h1>)}
            {!(search && result) && <p className={`max-w-xl text-base sm:text-lg leading-relaxed text-body ${editorial ? "mt-5 text-left" : search ? "mx-auto mt-3 max-w-2xl text-center text-base sm:text-lg lg:text-xl lg:leading-[1.45]" : "mx-auto mt-5 text-left sm:text-center"}`}>
              {editorial ? sh[1] : search ? (subtitle || sh[1]) : subtitle || <BrandText>{biz
                ? "Case management for travel agencies, study consultants and immigration firms. Sponsorship compliance for employers. Qualified applicants routed to you from this site."
                : `Straight answers on eligibility, documents, fees and timelines for ${DESTINATIONS.length} countries, checked against the rules each government publishes. Create a free profile and Vistolane files your application with a guaranteed filing date.`}</BrandText>}
            </p>}
          </motion.div>
        </AnimatePresence>

        <div className={`relative z-40 w-full max-w-3xl animate-fade-up delay-2 ${editorial ? "mt-9" : search ? "mx-auto mt-6" : "mx-auto mt-9"}`}>
          <div className={`relative flex items-center rounded-full border bg-background pl-4 pr-1.5 sm:pl-6 sm:pr-2 transition-[box-shadow,border-color] duration-300 ${search ? "h-14 sm:h-[64px]" : "h-[64px]"} ${
            focused ? "border-tint shadow-card" : "border-gray4 shadow-card hover:shadow-pop"}`}>
            <Search className="h-5 w-5 shrink-0 text-label2" />
            <div className="relative ml-3 min-w-0 flex-1">
              <input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setDdOpen(true); }}
                onFocus={() => { setFocused(true); setDdOpen(true); }} onBlur={() => { setFocused(false); setTimeout(() => setDdOpen(false), 150); }} onKeyDown={(e) => { if (keys.onKeyDown(e, query.trim())) return; if (e.key === "Enter") go(); }}
                role="combobox" aria-expanded={showDd} aria-controls="hero-search-results" aria-autocomplete="list"
                aria-label="Ask a visa question" data-testid="hero-ask-input"
                className="h-12 w-full bg-transparent text-base sm:text-lg text-foreground outline-none focus-visible:outline-none" />
              {!query && (
                <div className="pointer-events-none absolute inset-0 flex items-center overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.span key={`${persona}-${tw.g}-${tw.q}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                      className="truncate text-base sm:text-lg text-label2">
                      {prompt}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}
            </div>
            {showDd && <SearchDropdown q={query} items={srch.items} busy={srch.busy} active={keys.active} onPick={pickResult} onAsk={askFromSearch} testId="hero-search" />}
            <button onClick={go} data-testid="hero-ask-btn" data-verb={group.verb}
              className="ml-2 inline-flex h-11 min-w-[88px] shrink-0 items-center justify-center gap-2 rounded-full bg-tint px-5 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-tint-strong active:scale-95 sm:ml-3 sm:h-12 sm:min-w-[96px] sm:px-6">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={group.verb} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className="inline-flex items-center gap-2">
                  {group.verb}{!isAsk && <ArrowRight className="h-4 w-4" />}
                </motion.span>
              </AnimatePresence>
            </button>
          </div>

          {!biz && !result && (
            <div className="mt-4 flex justify-center animate-fade-up delay-3" data-testid="hero-appointments">
              <Link to="/appointments" data-testid="hero-appointments-btn"
                className="group inline-flex h-11 max-w-full items-center gap-2 rounded-full border border-separator bg-background px-4 text-sm font-medium text-foreground shadow-card transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-foreground hover:shadow-pop sm:px-5">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tint/10 text-tint-strong transition-colors group-hover:bg-tint group-hover:text-white"><CalendarDays className="h-4 w-4" /></span>
                <span className="truncate">Open visa appointment dates across India</span>
                <span className="hidden shrink-0 items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success-strong sm:inline-flex"><i className="h-1.5 w-1.5 animate-pulse rounded-full bg-success-strong" /> Live</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-label2 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          )}

          {biz && ctas !== null && (
            <div className="mt-6 flex flex-wrap justify-center gap-3" data-testid="hero-b2b-ctas">
              <Link to={ctas?.[0]?.[1] || "/contact"} className={btnPrimary} data-testid="hero-book-demo">{ctas?.[0]?.[0] || "Book a demo"} <ArrowRight className="h-4 w-4" /></Link>
              <Link to={ctas?.[1]?.[1] || "/business/pricing"} className={btnGhost} data-testid="hero-see-plans">{ctas?.[1]?.[0] || "See workspace plans"} <ArrowRight className="h-4 w-4" /></Link>
            </div>
          )}
        </div>

        <AnimatePresence>
          {result && (
            <motion.div key="answer" initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`relative z-40 mx-auto mt-8 rounded-card border border-separator bg-background p-7 sm:p-9 text-left shadow-card ${search ? "max-w-5xl" : "max-w-3xl"}`} data-testid="hero-answer">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-medium text-tint-strong">Answer</p>
                {result.streaming && <span className="rounded-full bg-gray6 px-2.5 py-1 text-xs font-medium text-label2">Answering…</span>}
              </div>
              <p className="mt-3 font-display text-2xl leading-snug text-foreground">{result.q}</p>
              <Rich text={result.answer || "…"} className="mt-4 text-base leading-relaxed text-body" />
              <DataViz chart={result.chart} stats={result.stats} />
              <Citations items={result.citations} scope={result.scope} />
              {!!result.followups?.length && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="answer-followups">
                  {result.followups.slice(0, 3).map((f) => <button key={f} onClick={() => ask(f)} className="rounded-full bg-gray6 px-3.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-gray5">{f}</button>)}
                </div>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to={result.cta?.to || "/eligibility"} className={btnPrimary} data-testid="answer-eligibility-btn">{result.cta?.label || "Check my eligibility"} <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/destinations" className={btnGhost}>Open destinations <ArrowUpRight className="h-4 w-4" /></Link>
                <button onClick={reset} className="inline-flex h-11 items-center px-3 text-sm font-semibold text-tint-strong hover:underline" data-testid="answer-reset-btn">Ask another</button>
                <ShareButton biz={biz} compact testId="answer-share" className="h-11" context={{ kind: "answer", label: "search", result }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {below}
        {editorial && !result && (
          <div className="mt-6 animate-fade-up delay-3" data-testid="hero-popular">
            <p className="text-xs font-semibold text-label2">Try a question</p>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {groups.flatMap((g) => g.questions.slice(0, 2).map((qq) => ({ ...qq, verb: g.verb, to: qq.to || g.to }))).slice(0, 6).map((qq) => (
                <li key={qq.text}>
                  <button type="button" onClick={() => (qq.verb === "Ask" ? ask(qq.text) : navigate(qq.to))} data-testid="hero-popular-item"
                    className="group flex h-12 w-full items-center gap-3 rounded-xl border border-separator bg-background px-3.5 text-left text-sm text-foreground shadow-card transition-[border-color,transform,box-shadow] hover:-translate-y-0.5 hover:border-foreground hover:shadow-pop">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gray6 text-label2 group-hover:bg-primary group-hover:text-white"><Search className="h-3.5 w-3.5" /></span>
                    <span className="min-w-0 flex-1 truncate">{cap(qq.text).replace(/[.?]$/, "")}</span>
                    <span className="shrink-0 rounded-full bg-gray6 px-2 py-0.5 text-xs font-semibold text-label2 group-hover:bg-tint/10 group-hover:text-tint-strong">{qq.verb}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
        {aside && <div className="mt-8 lg:mt-0 animate-fade-up delay-3" data-testid="hero-aside">{aside}</div>}
        {news && <div className="relative z-30 mx-auto mt-14 max-w-5xl rounded-card bg-gray6/60 px-4 py-5 sm:px-6 animate-fade-up delay-3" data-testid="hero-news-section">
          <NewsStrip items={newsItems} />
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Newsletter biz={biz} />
            <ShareButton biz={biz} context={{ kind: "news", label: "news", items: newsItems }} />
          </div>
          <p className="mt-2 text-center text-xs text-label2" data-testid="newsletter-hint">Free, weekly. Be first on rule changes, job alerts and new visa-free trips. Share sends the latest headlines by email, WhatsApp, SMS or PDF.</p>
        </div>}
      </div>
    </section>
  );
}
